import {
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  Timestamp,
} from 'firebase/firestore';

import { db } from 'utils/firebaseConfig';

export type UserAccessStatus = 'pending' | 'approved' | 'rejected';

export interface UserAccessRecord {
  uid: string;
  email: string;
  status: UserAccessStatus;
  requestedAt: string;
  updatedAt: string;
  reviewedBy: string;
}

const USERS_COLLECTION = 'users';

const toISOString = (value: unknown): string => {
  if (value instanceof Timestamp) {
    return value.toDate().toISOString();
  }

  if (
    value &&
    typeof value === 'object' &&
    'toDate' in value &&
    typeof (value as { toDate?: () => Date }).toDate === 'function'
  ) {
    return (value as { toDate: () => Date }).toDate().toISOString();
  }

  if (typeof value === 'string') {
    return value;
  }

  return '';
};

const toSafeString = (value: unknown): string => {
  return typeof value === 'string' ? value : '';
};

const normalizeAccessRecord = (uid: string, raw: Record<string, unknown>): UserAccessRecord => ({
  uid,
  email: toSafeString(raw.email),
  status: (raw.status as UserAccessStatus) || 'pending',
  requestedAt: toISOString(raw.requestedAt),
  updatedAt: toISOString(raw.updatedAt),
  reviewedBy: toSafeString(raw.reviewedBy),
});

export const getUserAccessRecord = async (uid: string): Promise<UserAccessRecord | null> => {
  const userDocRef = doc(db, USERS_COLLECTION, uid);
  const snapshot = await getDoc(userDocRef);

  if (!snapshot.exists()) {
    return null;
  }

  return normalizeAccessRecord(uid, snapshot.data());
};

export const requestUserAccess = async ({ uid, email }: { uid: string; email: string }): Promise<void> => {
  const userDocRef = doc(db, USERS_COLLECTION, uid);
  await setDoc(
    userDocRef,
    {
      email,
      status: 'pending',
      requestedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );
};

export const listUserAccessRecords = async (): Promise<UserAccessRecord[]> => {
  const usersRef = collection(db, USERS_COLLECTION);
  const usersQuery = query(usersRef, orderBy('updatedAt', 'desc'));
  const querySnapshot = await getDocs(usersQuery);

  return querySnapshot.docs.map((snapshot) =>
    normalizeAccessRecord(snapshot.id, snapshot.data() as Record<string, unknown>)
  );
};

export const updateUserAccessStatus = async ({
  uid,
  status,
  reviewedBy,
}: {
  uid: string;
  status: UserAccessStatus;
  reviewedBy: string;
}): Promise<void> => {
  const userDocRef = doc(db, USERS_COLLECTION, uid);

  await setDoc(
    userDocRef,
    {
      status,
      reviewedBy,
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );
};
